// The form is a 4-step wizard instead of one long scroll: the rule model got big enough that a
// flat drawer buried the fields that matter. Steps 1 and 2 are the minimum viable rule, so saving
// is allowed from step 2 onwards; steps 3 and 4 are guards that can be filled in later.
//
// `errorKeys` maps each step to the keys its fields publish into `dsRepricingDrawerErrors`, which
// is what lets the footer block Next on the current step without dragging in unrelated steps.
// `kicker` is the verb that names each step's role in the story of a rule:
// When → What → How → Then. Rendered above the label in the stepper.
export const DS_REPRICING_STEPS = [
	{
		step: 1,
		kicker: 'WHEN',
		label: 'Basic',
		sub: 'Identity and when the rule applies',
		// `leadTimes` and `weekdays` live here, not in step 3: they are ways of saying when the rule
		// applies, so they sit with Season and Duration. The three are exclusive and the inactive ones
		// publish valid, so all the keys can be listed unconditionally.
		// No 'weekdays': it is an optional filter now, so it has no invalid state to report.
		errorKeys: ['name', 'priority', 'seasons', 'durations', 'leadTimes'],
	},
	{
		step: 2,
		kicker: 'WHAT',
		// "Scope", not "Market scope": the step holds what the rule prices (our
		// offices, our faces, our vehicles) as well as what it is compared
		// against, and a step titled "market" labelled everything inside it
		// as market — which is how "Insurance" would have read as "compare
		// only against this insurance" (CU-869fe6mf4, UX decision 2).
		label: 'Scope',
		sub: 'What this rule prices, and which market it is compared against',
		// Scope fields never fail validation today, but the step still lists the keys so future
		// per-field errors show up as an invalid step in the stepper.
		errorKeys: ['filterGroupOffices', 'filterGroupSites', 'filterGroupWebSuppliers',
		            'filterGroupProviders', 'filterAcriss', 'filterCategories',
		            'filterTariffTypes', 'filterInsurances'],
	},
	{
		step: 3,
		kicker: 'HOW',
		label: 'Pricing strategy',
		sub: 'Market Trend or Ranking',
		errorKeys: ['strategy', 'undercutValue'],
	},
	{
		step: 4,
		kicker: 'THEN',
		label: 'Tethering',
		sub: 'Pick which tethers from Setup should apply',
		optional: true,
		errorKeys: [],
	},
	{
		step: 5,
		kicker: 'IF',
		label: 'My Business',
		sub: 'PMS activation conditions (fleet, pace, forecast…)',
		optional: true,
		errorKeys: [],
	},
]

export const DS_REPRICING_TOTAL_STEPS = DS_REPRICING_STEPS.length

// Saving needs identity + scope + strategy in place; Extras are optional by design.
export const FIRST_SAVEABLE_STEP = 3

const isOk = value => value !== false

export const stepIsValid = (step, errors) => {
	const definition = DS_REPRICING_STEPS.find(s => s.step === step)
	if (!definition) return true
	return definition.errorKeys.every(key => isOk(errors?.[key]))
}

// Every panel stays mounted (just hidden), so an untouched step still publishes its errors and a
// broken optional guard cannot slip through an early save.
export const formIsValid = errors => Object.values(errors ?? {}).every(isOk)